import { AuthenticationService } from './authService';
import { 
  getCurrentUserFromLocalStorage, 
  getI18nLanguageInLocalStorage,
  setI18nLanguageInLocalStorage
} from '../helpers/localStorageHelper';
import { postChangeLanguage } from './userService';

/**
 * Language Initialization Service
 * Handles language detection, user sync and persistence
 */
export class LanguageInitializationService {
  constructor() {
    this.langs = [];
    this.defaultLang = '';
    this.fallbackLang = '';
    this.routeLang = '';
  }
  /**
   * Initialize with server-provided data
   */
  initialize(props = {}) {
    this.langs = props.supportedLanguages || [];
    this.defaultLang = props.defaultLanguage || '';
    this.fallbackLang = props.fallbackLanguage || '';
    this.routeLang = props.routeLang || '';
  }

  getRouteLang() {
    return this.routeLang;
  }

  getUserLanguage() {
    try {
      const currentUser = AuthenticationService.instance().getCurrentUser();
      if (currentUser?.defaultLanguage) return currentUser.defaultLanguage;
    } catch {}

    try {
      return getCurrentUserFromLocalStorage()?.defaultLanguage || null;
    } catch {
      return null;
    }
  }

  isUserLoggedIn() {
    try {
      const currentUser = AuthenticationService.instance().getCurrentUser();
      return currentUser?.userName ? true : false;
    } catch {
      return false;
    }
  }

  isValidLang(lang) {
    return !!lang && this.langs.includes(lang);
  }

  /**
   * Persist user language change via API + localStorage
   */
  async updateUserLanguage(langCode) {
    try {
      const currentUser = AuthenticationService.instance().getCurrentUser();
      if (!currentUser?.userName) return;

      currentUser.defaultLanguage = langCode;
      AuthenticationService.instance().setCurrentUser(currentUser);
      await postChangeLanguage({ language: langCode });
      setI18nLanguageInLocalStorage(langCode);
    } catch (err) {
      // Silent fallback - don't crash the page
    }
  }

  /**
   * Get target language with fallbacks
   */
  getTargetLanguage() {
    const userLang = this.getUserLanguage();
    if (this.isValidLang(userLang)) return userLang;
    if (this.isValidLang(this.defaultLang)) return this.defaultLang;
    if (this.isValidLang(this.fallbackLang)) return this.fallbackLang;
    return '';
  }

  /**
   * Navigate to language and sync localStorage
   */
  navigateAndSync(lang) {
    if (!this.isValidLang(lang)) return;
    setI18nLanguageInLocalStorage(lang);
    this.navigateToLanguage(lang);
  }

  /**
   * Navigate to correct language route
   */
  navigateToLanguage(lang) {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    
    const currentLang = segments[0];
    const hasLangPrefix = this.isValidLang(currentLang);
    
    if (hasLangPrefix && currentLang === lang) return false;
    
    if (hasLangPrefix) {
      segments[0] = lang;
    } else {
      segments.unshift(lang);
    }
    
    const newPath = '/' + segments.join('/') + window.location.search;
    window.location.replace(newPath);
    return true;
  }

  /**
   * Sync user language with routeLang if they differ (for logged in users)
   */
  async syncWithRoute() {
    if (!this.isUserLoggedIn() || !this.isValidLang(this.routeLang)) return false;

    const userLang = this.getUserLanguage();
    if (!userLang || userLang === this.routeLang) return false;

    await this.updateUserLanguage(this.routeLang);
    return true;
  }

  /**
   * Handle route language for non-logged in users
   */
  handleRouteLanguage(localStorageLang) {
    if (!this.isValidLang(localStorageLang) || localStorageLang !== this.routeLang) {
      setI18nLanguageInLocalStorage(this.routeLang);
    }
  }

  /**
   * Handle empty route for non-logged in users
   */
  handleEmptyRoute(localStorageLang) {
    if (this.isValidLang(localStorageLang)) {
      this.navigateAndSync(localStorageLang);
    } else {
      const targetLang = this.getTargetLanguage();
      if (targetLang) {
        this.navigateAndSync(targetLang);
      }
    }
  }

  /**
   * Handle language logic for non-logged in users
   */
  async handleNonLoggedInUser() {
    const localStorageLang = getI18nLanguageInLocalStorage();
    if (this.isValidLang(this.routeLang)) {
      this.handleRouteLanguage(localStorageLang);
    } else {
      this.handleEmptyRoute(localStorageLang);
    }
  }

  /**
   * Handle language logic for logged in users
   */
  async handleLoggedInUser() {
    const synced = await this.syncWithRoute();
    
    if (!synced && !this.isValidLang(this.routeLang)) {
      const targetLang = this.getTargetLanguage();
      if (targetLang) {
        this.navigateAndSync(targetLang);
      }
    }
  }

  /**
   * Main execution
   */
  async execute() {
    if (this.isUserLoggedIn()) {
      await this.handleLoggedInUser();
    } else {
      await this.handleNonLoggedInUser();
    }
    return true;
  }
}
